import axios from "axios";
import {
  CountryTransfertBank,
  CountryTransfertItem,
  CountryTransfertOperator,
  CountryTransfert,
  CountryTransfertEmail,
} from "../../types/features/administration/CountryTransfert";
import { BACKEND_URL } from "../../config/api";

export const countryTransfertService = {
  async createCountryTransfert(
    token: string,
    data: { country_sender_id: number; country_destination_id: number; rate: string }
  ) {
    try {
      await axios.post(`${BACKEND_URL}/country-transfert/`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      throw error;
    }
  },
  async getCountryTransferts(limit: any, skip: any): Promise<CountryTransfert[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/country-transfert/?skip=${skip}&limit=${limit}`);
      const data = response.data;
      return data.data.country_transferts;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async updateCountryTransfert(
    token: string,
    id: any,
    data: { country_sender_id?: number; country_destination_id?: number; rate?: string }
  ) {
    try {
      await axios.put(`${BACKEND_URL}/country-transfert/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async deleteCountryTransfert(token: string, id: string) {
    try {
      await axios.delete(`${BACKEND_URL}/country-transfert/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async getCountryTransfertItems(countryTransfertId: number): Promise<CountryTransfertItem[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/country-transfert-item/${countryTransfertId}`);
      return response.data.data.items || [];
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async createCountryTransfertItem(
    token: string,
    data: { country_transfert_id: number; name: string; url?: string }
  ): Promise<CountryTransfertItem> {
    const response = await axios.post(`${BACKEND_URL}/country-transfert-item/`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data.item;
  },
  async updateCountryTransfertItem(
    token: string,
    id: number,
    data: { name?: string; url?: string }
  ): Promise<CountryTransfertItem> {
    const response = await axios.put(`${BACKEND_URL}/country-transfert-item/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data.item;
  },
  async deleteCountryTransfertItem(token: string, id: number) {
    await axios.delete(`${BACKEND_URL}/country-transfert-item/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  // ── Operators ──────────────────────────────────────────────────────────
  async getCountryTransfertOperators(countryTransfertId: number): Promise<CountryTransfertOperator[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/country-transfert-operator/${countryTransfertId}`);
      return response.data.data.operators || [];
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async createCountryTransfertOperator(token: string, data: FormData): Promise<CountryTransfertOperator> {
    const response = await axios.post(`${BACKEND_URL}/country-transfert-operator/`, data, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    });
    return response.data.data.operator;
  },
  async updateCountryTransfertOperator(token: string, id: number, data: FormData): Promise<CountryTransfertOperator> {
    const response = await axios.put(`${BACKEND_URL}/country-transfert-operator/${id}`, data, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    });
    return response.data.data.operator;
  },
  async deleteCountryTransfertOperator(token: string, id: number) {
    await axios.delete(`${BACKEND_URL}/country-transfert-operator/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  // ── Banks ──────────────────────────────────────────────────────────────
  async getCountryTransfertBanks(countryTransfertId: number): Promise<CountryTransfertBank[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/country-transfert-bank/${countryTransfertId}`);
      return response.data.data.banks || [];
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async createCountryTransfertBank(token: string, data: FormData): Promise<CountryTransfertBank> {
    const response = await axios.post(`${BACKEND_URL}/country-transfert-bank/`, data, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    });
    return response.data.data.bank;
  },
  async updateCountryTransfertBank(token: string, id: number, data: FormData): Promise<CountryTransfertBank> {
    const response = await axios.put(`${BACKEND_URL}/country-transfert-bank/${id}`, data, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    });
    return response.data.data.bank;
  },
  async deleteCountryTransfertBank(token: string, id: number) {
    await axios.delete(`${BACKEND_URL}/country-transfert-bank/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  // ── Emails ─────────────────────────────────────────────────────────────
  async getCountryTransfertEmails(countryTransfertId: number): Promise<CountryTransfertEmail[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/country-transfert-email/${countryTransfertId}`);
      return response.data.data.emails || [];
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async createCountryTransfertEmail(
    token: string,
    data: { country_transfert_id: number; name: string; email: string }
  ): Promise<CountryTransfertEmail> {
    const response = await axios.post(`${BACKEND_URL}/country-transfert-email/`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data.email;
  },
  async updateCountryTransfertEmail(
    token: string,
    id: string,
    data: { name?: string; email?: string }
  ): Promise<CountryTransfertEmail> {
    const response = await axios.put(`${BACKEND_URL}/country-transfert-email/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data.email;
  },
  async deleteCountryTransfertEmail(token: string, id: string) {
    await axios.delete(`${BACKEND_URL}/country-transfert-email/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};
